import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../components/Button.js';
import { Textarea } from '../components/Input.js';
import { api, formatApiError } from '../lib/api.js';
import { buildTitle, useDocumentTitle } from '../lib/use-document-title.js';
import { MAX_PROMPT_LENGTH, type EffortLevel, type PermissionMode } from '@claude-remote/shared';

const MODES: Array<{ value: PermissionMode; label: string; hint: string }> = [
  { value: 'default', label: 'Default', hint: 'Запрашивать разрешение на каждый тул' },
  { value: 'acceptEdits', label: 'Accept edits', hint: 'Авто-одобрение правок файлов' },
  { value: 'auto', label: 'Auto', hint: 'Edits + read-only Bash auto, опасное — спросит' },
  { value: 'plan', label: 'Plan only', hint: 'Без исполнения, только планирование' },
  { value: 'bypassPermissions', label: 'Bypass ⚠️', hint: 'Полный автопилот (осторожно)' },
];

type ModelKind = 'opus' | 'sonnet' | 'haiku';
type ContextSize = '200k' | '1m';

const MODEL_LABELS: Record<ModelKind, string> = {
  opus: 'Opus 4.7',
  sonnet: 'Sonnet 4.6',
  haiku: 'Haiku 4.5',
};

const EFFORT_LABELS: Array<{ value: EffortLevel; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'extra_high', label: 'Extra high' },
  { value: 'max', label: 'Max' },
];

/** Собрать ID модели для SDK. 1M-контекст есть только у Opus. */
function buildModelId(kind: ModelKind, context: ContextSize): string {
  if (kind === 'sonnet') return 'claude-sonnet-4-6';
  if (kind === 'haiku') return 'claude-haiku-4-5';
  return context === '1m' ? 'claude-opus-4-7[1m]' : 'claude-opus-4-7';
}

export function NewSessionScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Инициализируем промпт из query-параметра (если пользователь пришёл из PlanPicker).
  const [prompt, setPrompt] = useState(() => searchParams.get('prompt') ?? '');
  const [mode, setMode] = useState<PermissionMode>('default');
  const [modelKind, setModelKind] = useState<ModelKind>('opus');
  const [context, setContext] = useState<ContextSize>('200k');
  const [effort, setEffort] = useState<EffortLevel>('high');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [projectName, setProjectName] = useState<string | null>(null);

  // Тянем имя проекта только ради корректного <title> — на самом экране
  // оно не отображается, поэтому ошибки запроса просто игнорим.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    void api
      .get<{ name: string }>(`/api/v1/projects/${projectId}`)
      .then((p) => {
        if (!cancelled) setProjectName(p.name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useDocumentTitle(buildTitle(projectName, 'Новая сессия'));

  const promptLength = prompt.length;
  const isOverLimit = promptLength > MAX_PROMPT_LENGTH;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!projectId) return;
    if (isOverLimit) {
      setError(
        `Промпт слишком длинный: ${promptLength.toLocaleString('ru-RU')} из ${MAX_PROMPT_LENGTH.toLocaleString('ru-RU')} символов`,
      );
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const model = buildModelId(modelKind, context);
      const res = await api.post<{ sessionId: string }>(
        `/api/v1/sessions/project/${projectId}`,
        { prompt, permissionMode: mode, model, effort },
      );
      navigate(`/sessions/${res.sessionId}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto">
      <header className="flex items-center pt-2">
        <Link
          to={`/projects/${projectId}`}
          className="inline-flex items-center text-neutral-400 hover:text-neutral-200"
        >
          <ChevronLeft className="w-5 h-5" /> <span className="text-sm">Back</span>
        </Link>
      </header>
      <h1 className="text-2xl font-semibold">Новая сессия</h1>

      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1">
          <label className="text-xs uppercase text-neutral-500">Промпт</label>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Что нужно сделать…"
            rows={8}
            className="w-full"
            autoFocus
          />
          {/* Счётчик показываем только когда близко к лимиту (≥80%), чтобы не
              отвлекать в повседневном случае. */}
          {promptLength >= MAX_PROMPT_LENGTH * 0.8 && (
            <div
              className={`text-xs text-right tabular-nums ${
                isOverLimit ? 'text-red-400' : 'text-neutral-500'
              }`}
            >
              {promptLength.toLocaleString('ru-RU')} / {MAX_PROMPT_LENGTH.toLocaleString('ru-RU')}
            </div>
          )}
        </div>

        <div className="space-y-1">
          <label className="text-xs uppercase text-neutral-500">Permission mode</label>
          <div className="grid grid-cols-2 gap-2">
            {MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setMode(m.value)}
                className={`text-left rounded-xl border p-3 transition-colors ${
                  mode === m.value
                    ? 'bg-blue-600/15 border-blue-600/50'
                    : 'bg-neutral-900/60 border-neutral-800 hover:border-neutral-700'
                }`}
              >
                <div className="text-sm font-medium">{m.label}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{m.hint}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-xs uppercase text-neutral-500">Модель</label>
          <select
            value={modelKind}
            onChange={(e) => setModelKind(e.target.value as ModelKind)}
            className="w-full h-11 px-3 rounded-xl bg-neutral-900 border border-neutral-800"
          >
            <option value="opus">{MODEL_LABELS.opus}</option>
            <option value="sonnet">{MODEL_LABELS.sonnet}</option>
            <option value="haiku">{MODEL_LABELS.haiku}</option>
          </select>
        </div>

        {modelKind === 'opus' && (
          <div className="space-y-1">
            <label className="text-xs uppercase text-neutral-500">Контекст</label>
            <div className="grid grid-cols-2 gap-2">
              {(['200k', '1m'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setContext(c)}
                  className={`text-left rounded-xl border p-3 transition-colors ${
                    context === c
                      ? 'bg-blue-600/15 border-blue-600/50'
                      : 'bg-neutral-900/60 border-neutral-800 hover:border-neutral-700'
                  }`}
                >
                  <div className="text-sm font-medium">
                    {c === '200k' ? '200k' : '1M'}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {c === '200k' ? 'Стандарт' : 'Расширенный — 2× цена'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-1">
          <label className="text-xs uppercase text-neutral-500">Effort</label>
          <select
            value={effort}
            onChange={(e) => setEffort(e.target.value as EffortLevel)}
            className="w-full h-11 px-3 rounded-xl bg-neutral-900 border border-neutral-800"
          >
            {EFFORT_LABELS.map((e) => (
              <option key={e.value} value={e.value}>
                {e.label}
              </option>
            ))}
          </select>
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}

        <Button
          type="submit"
          size="lg"
          className="w-full"
          disabled={loading || prompt.trim().length === 0 || isOverLimit}
        >
          {loading ? 'Запускаем…' : 'Start'}
        </Button>
      </form>
    </div>
  );
}
